import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { createPortal } from "react-dom";
import { PlusIcon } from "../../assets/icons";
import CustomTable from "../../components/table/CustomTable";
import CommonButton from "../../components/commonButton";
import DeletePopup from "../../components/delete-popup";
import ConfirmModal from "../../components/withModal/ConfirmModal";
import { usePermission } from "../../hooks/usePermission";
import { useToast } from "../../context/TostContext";
import {
    toggleProfileSuggestionCardStatus,
    deleteProfileSuggestionCard,
    reorderProfileSuggestionCard,
} from "../../redux/slice/profileSuggestionCardSlice";
import { TableSearch } from "../../components/form/Fields";
import Input from "../../components/form";
import StatusChip from "../../components/statusChip/StatusChip";

const MODULE_SLUG = "profile_suggestion_cards";

const List = ({
    data,
    isLoading,
    isInitialLoad,
    searchTerm,
    setSearchTerm,
    setCurrentPage,
    currentPage,
    handleAddBtn,
    handleEditBtn,
    onView,
    refresh,
    limit,
    setLimit,
    total,
}) => {
    const dispatch = useDispatch();
    const toast = useToast();

    const [items, setItems] = useState([]);

    useEffect(() => {
        setItems(data || []);
    }, [data]);

    const handleDragEnd = async (result) => {
        if (!result.destination) return;
        if (result.source.index === result.destination.index) return;
        if (!canUpdate) {
            toast.error("You don't have permission to reorder suggestions");
            return;
        }

        const reorderedItems = Array.from(items);
        const [removed] = reorderedItems.splice(result.source.index, 1);
        reorderedItems.splice(result.destination.index, 0, removed);

        // Optimistic update
        const updatedWithOrders = reorderedItems.map((item, index) => ({
            ...item,
            sort_order: index + 1
        }));
        setItems(updatedWithOrders);

        try {
            console.log("updated list => ", updatedWithOrders)
        } catch (error) {
            console.error(error)
        }
    };

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusItem, setStatusItem] = useState(null);

    const { hasPermission } = usePermission();
    const canCreate = hasPermission(MODULE_SLUG, "create");
    const canUpdate = hasPermission(MODULE_SLUG, "update");
    const canDelete = hasPermission(MODULE_SLUG, "delete");

    const handleConfirmDelete = async () => {
        if (!selectedId) return;
        try {
            setIsSubmitting(true);
            const result = await dispatch(deleteProfileSuggestionCard(selectedId)).unwrap();
            if (result.res.success) {
                toast.success(result.res.message || "Profile suggestion card deleted successfully");
                setOpenDeleteModal(false);
                setSelectedId(null);
                refresh();
            }
        } catch (error) {
            toast.error(error.message || "Failed to delete card");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmStatusChange = async () => {
        if (!statusItem) return;
        try {
            const result = await dispatch(
                toggleProfileSuggestionCardStatus({
                    id: statusItem.id,
                    is_active: !statusItem.is_active,
                })
            ).unwrap();
            if (result.res.success) {
                toast.success(result.res.message || "Status updated successfully");
                refresh();
            }
        } catch (error) {
            toast.error(error.message || "Failed to update status");
        } finally {
            setShowStatusModal(false);
            setStatusItem(null);
        }
    };

    const actionList = [
        {
            title: "View",
            handler: (item) => {
                onView && onView(item);
            },
        },
        {
            title: "Edit",
            isVisible: () => canUpdate,
            handler: (item) => handleEditBtn(item),
        },
        {
            title: (item) => (item?.is_active ? "Deactivate" : "Activate"),
            isVisible: () => canUpdate,
            handler: (item) => {
                setStatusItem(item);
                setShowStatusModal(true);
            },
        },
        {
            title: "Delete",
            isVisible: () => canDelete,
            handler: (item) => {
                setSelectedId(item.id);
                setOpenDeleteModal(true);
            },
        },
    ];

    const columns = [
        {
            id: "media",
            header: "MEDIA",
            cell: (_, item) => (
                <div style={{ width: "80px" }}>
                    {item?.file?.url ? (
                        item.file.mimetype?.startsWith("video/") ? (
                            <video
                                src={item.file.url}
                                style={{
                                    width: "80px",
                                    height: "40px",
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                }}
                                muted
                            />
                        ) : (
                            <img
                                src={item.file.url}
                                alt="Suggestion Media"
                                style={{
                                    width: "80px",
                                    height: "40px",
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                }}
                            />
                        )
                    ) : (
                        "-"
                    )}
                </div>
            ),
        },
        {
            id: "key",
            header: "KEY",
            cell: (_, item) => item?.key || "-",
        },
        {
            id: "title",
            header: "TITLE",
            cell: (_, item) => item?.title || "-",
        },
        {
            id: "cta_text",
            header: "CTA TEXT",
            cell: (_, item) => item?.cta_text || "-",
        },
        {
            id: "cta_action",
            header: "CTA ACTION",
            cell: (_, item) => item?.cta_action || "-",
        },
        {
            id: "order",
            header: "ORDER",
            cell: (_, item) => (
                <div className="bg-white rounded-2" style={{ width: "80px" }} onClick={(e) => e.stopPropagation()}>
                    <Input
                        type="number"
                        className="py-1 px-2"
                        defaultValue={item?.sort_order || 0}
                        onBlur={async (e) => {
                            const newOrder = Number(e.target.value);
                            if (newOrder !== item.sort_order) {
                                try {
                                    const res = await dispatch(
                                        reorderProfileSuggestionCard({ id: item.id, sort_order: newOrder })
                                    ).unwrap();
                                    if (res.success) {
                                        toast.success("Order updated successfully");
                                        refresh();
                                    }
                                } catch (err) {
                                    toast.error(err.message || "Failed to update order");
                                }
                            }
                        }}
                    />
                </div>
            ),
        },
        {
            id: "status",
            header: "STATUS",
            cell: (_, item) => (
                <StatusChip
                    bg={item?.is_active ? "success" : "danger"}
                    pill
                    title={item?.is_active ? "Active" : "Inactive"}
                    className={canUpdate ? "cursor-pointer" : ""}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (canUpdate) {
                            setStatusItem(item);
                            setShowStatusModal(true);
                        }
                    }}
                />
            ),
        },
    ];

    const pagination = {
        currentPage,
        totalPages: Math.ceil(total / limit),
        total: total,
        onPageChange: (page) => setCurrentPage(page),
        pageSize: limit,
        setPageSize: (newLimit) => setLimit(newLimit),
        setCurrentPage,
        perPageOptions: [10, 20, 50, 100],
    };

    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary" style={{ fontSize: "18px" }}>
                Profile Suggestions
            </h4>
        </div>
    );

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <div className="p-3 border-bottom d-flex flex-wrap align-items-center gap-3 bg-white justify-content-between">
                <TableSearch value={searchTerm}
                    placeholder="Search by key or title..."
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }} onClear={() => {
                        setSearchTerm("");
                        setCurrentPage(1);
                    }} />
                {canCreate && (
                    <div className="action-buttons-container ms-auto">
                        <CommonButton
                            icon={<PlusIcon />}
                            variant="primary"
                            title="Add Profile Suggestion"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAddBtn();
                            }}
                        />
                    </div>
                )}
            </div>

            <CustomTable
                data={items}
                columns={columns}
                keyField="id"
                isDragable={canUpdate}
                onDragEnd={handleDragEnd}
                actionList={actionList}
                module={MODULE_SLUG}
                emptyMessage="No profile suggestions found."
                loading={isLoading && !isInitialLoad}
                tbody={{ trClass: "cursor-pointer" }}
                pagination={pagination}
                rowClick={(item) => {
                    onView && onView(item);
                }}
            />

            <DeletePopup
                show={openDeleteModal}
                onHide={() => {
                    setOpenDeleteModal(false);
                    setSelectedId(null);
                }}
                content="Are you sure you want to delete this profile suggestion card?"
                removeTitle="Cancel"
                onConfirm={handleConfirmDelete}
                disabled={isSubmitting}
                saveTitle={isSubmitting ? "Deleting..." : "Delete"}
            />

            <ConfirmModal
                isOpen={showStatusModal}
                onClose={() => {
                    setShowStatusModal(false);
                    setStatusItem(null);
                }}
                title={statusItem?.is_active ? "Deactivate Suggestion" : "Activate Suggestion"}
                message={
                    statusItem?.is_active
                        ? "Are you sure you want to deactivate this profile suggestion card?"
                        : "Are you sure you want to activate this profile suggestion card?"
                }
                onConfirm={handleConfirmStatusChange}
            />
        </>
    );
};

export default List;
